import { useCallback, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

// The shared GCP-console-style create surface. Replaces cramped modals with a
// dedicated page: breadcrumb/back, a roomy content column, an optional left
// step rail (for multi-step wizards), and a sticky footer for the actions.
// Single-page creates omit `steps` and render their sections as children.

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

// useStepper tracks the current step + the furthest reached, so a user can jump
// back to a completed step but never skip ahead past a step's validation.
export function useStepper(count: number) {
  const [current, setCurrent] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const next = useCallback(() => {
    setCurrent((c) => {
      const n = Math.min(c + 1, count - 1);
      setFurthest((f) => Math.max(f, n));
      return n;
    });
  }, [count]);
  const back = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);
  const goTo = useCallback((i: number) => setCurrent((c) => (i <= furthest && i >= 0 ? i : c)), [furthest]);
  return {
    current,
    furthest,
    next,
    back,
    goTo,
    isFirst: current === 0,
    isLast: current === count - 1,
  };
}

export function CreatePage({
  title,
  description,
  backTo,
  steps,
  current = 0,
  furthest = 0,
  onJump,
  footer,
  children,
  // wide widens the page (e.g. a browse-a-gallery step needs more room than a
  // form). Defaults to the standard form width.
  wide = false,
}: {
  title: string;
  description?: string;
  backTo: string;
  steps?: WizardStep[];
  current?: number;
  furthest?: number;
  onJump?: (i: number) => void;
  footer: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  const maxW = wide ? "max-w-6xl" : "max-w-4xl";
  return (
    <div className={cn("mx-auto w-full", maxW)}>
      <Link
        to={backTo}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>

      <div className={cn(steps ? "grid grid-cols-1 gap-8 md:grid-cols-[210px_1fr]" : "")}>
        {steps && <StepRail steps={steps} current={current} furthest={furthest} onJump={onJump} />}
        <div className="min-w-0 space-y-6 pb-4">{children}</div>
      </div>

      {/* Sticky action bar — sticks to the viewport bottom within the scroll
          container; the negative margins bleed it to the content padding. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-8 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 lg:-mx-10">
        <div className={cn("mx-auto flex w-full items-center gap-3", maxW)}>{footer}</div>
      </div>
    </div>
  );
}

function StepRail({
  steps,
  current,
  furthest,
  onJump,
}: {
  steps: WizardStep[];
  current: number;
  furthest: number;
  onJump?: (i: number) => void;
}) {
  return (
    <nav className="hidden md:block">
      <ol className="space-y-1">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const reachable = i <= furthest;
          return (
            <li key={s.id}>
              <button
                type="button"
                disabled={!reachable || !onJump}
                onClick={() => onJump?.(i)}
                className={cn(
                  "flex w-full items-start gap-3 rounded-md px-2 py-2 text-left",
                  active ? "bg-muted" : reachable ? "hover:bg-muted/50" : "opacity-50",
                  reachable && onJump ? "cursor-pointer" : "cursor-default",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium",
                    done
                      ? "border-primary bg-primary text-primary-foreground"
                      : active
                        ? "border-primary text-primary"
                        : "border-muted-foreground/40 text-muted-foreground",
                  )}
                >
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="min-w-0">
                  <span className={cn("block text-sm font-medium", active ? "text-foreground" : "text-muted-foreground")}>
                    {s.title}
                  </span>
                  {s.description && <span className="mt-0.5 block text-xs text-muted-foreground">{s.description}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// WizardFooter is the standard action row: Cancel (always, left) · Back (not
// first) · primary (Next, or the submit label on the last step).
export function WizardFooter({
  onCancel,
  onBack,
  onPrimary,
  isFirst,
  isLast,
  submitting,
  submitLabel = "Create",
  nextLabel = "Continue",
  primaryDisabled,
}: {
  onCancel: () => void;
  onBack?: () => void;
  onPrimary: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  submitLabel?: string;
  nextLabel?: string;
  primaryDisabled?: boolean;
}) {
  return (
    <>
      <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting} className="mr-auto">
        Cancel
      </Button>
      {!isFirst && onBack && (
        <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
          Back
        </Button>
      )}
      <Button type="button" onClick={onPrimary} disabled={submitting || primaryDisabled}>
        {submitting && <Spinner />} {isLast ? submitLabel : nextLabel}
      </Button>
    </>
  );
}

// SingleFooter is the footer for non-wizard (single-page) creates: Cancel +
// one primary button.
export function SingleFooter({
  onCancel,
  onPrimary,
  submitting,
  submitLabel = "Create",
  primaryDisabled,
}: {
  onCancel: () => void;
  onPrimary: () => void;
  submitting?: boolean;
  submitLabel?: string;
  primaryDisabled?: boolean;
}) {
  return (
    <>
      <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting} className="mr-auto">
        Cancel
      </Button>
      <Button type="button" onClick={onPrimary} disabled={submitting || primaryDisabled}>
        {submitting && <Spinner />} {submitLabel}
      </Button>
    </>
  );
}

// Section is a titled content block for a step/page — the GCP-style "card with
// a heading + helper text" grouping.
export function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
